/**
 * Fixes image orientation based on EXIF metadata.
 * Browsers apply EXIF orientation when drawing to canvas, so we draw and re-export
 * to produce a correctly oriented image regardless of upload source (phone, etc.).
 *
 * Re-encodes in the same raster format when possible. GIF is emitted as PNG (browsers
 * do not reliably encode GIF from canvas; PNG preserves transparency).
 *
 * @param {File} file - Image file (JPEG, PNG, WebP, GIF, etc.)
 * @returns {Promise<File>} Correctly oriented file (same type when supported)
 */
const MIME_JPEG = 'image/jpeg'
const MIME_PNG = 'image/png'
const MIME_WEBP = 'image/webp'
const MIME_GIF = 'image/gif'

/** @param {string} name */
function guessMimeFromFileName (name) {
  const n = String(name || '').toLowerCase()
  if (n.endsWith('.png')) return MIME_PNG
  if (n.endsWith('.webp')) return MIME_WEBP
  if (n.endsWith('.gif')) return MIME_GIF
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return MIME_JPEG
  return ''
}

/**
 * @param {File} file
 * @returns {{ outMime: string, ext: string, quality?: number }|null} null = skip re-encode (exotic types)
 */
function resolveOutputEncoding (file) {
  const raw = (file.type || '').toLowerCase()
  const fromName = guessMimeFromFileName(file.name)
  const mime = raw || fromName

  if (!mime.startsWith('image/')) {
    return null
  }

  if (mime === MIME_GIF) {
    return { outMime: MIME_PNG, ext: 'png' }
  }
  if (mime === MIME_PNG || mime === 'image/x-png') {
    return { outMime: MIME_PNG, ext: 'png' }
  }
  if (mime === MIME_WEBP) {
    return { outMime: MIME_WEBP, ext: 'webp', quality: 0.92 }
  }
  if (mime === MIME_JPEG || mime === 'image/jpg' || mime === 'image/pjpeg') {
    return { outMime: MIME_JPEG, ext: 'jpg', quality: 0.92 }
  }
  // e.g. HEIC, TIFF, AVIF - avoid converting client-side; server/preloader may handle
  return null
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} type
 * @param {number} [quality]
 */
function canvasToBlob (canvas, type, quality) {
  return new Promise((resolve) => {
    if (quality !== undefined && quality !== null) {
      canvas.toBlob((blob) => resolve(blob), type, quality)
    } else {
      canvas.toBlob((blob) => resolve(blob), type)
    }
  })
}

/** @param {File} file */
function isRasterImageFile (file) {
  if (!(file instanceof File)) return false
  if (file.type && file.type.startsWith('image/')) return true
  return guessMimeFromFileName(file.name) !== ''
}

export async function fixImageOrientation (file) {
  if (!isRasterImageFile(file)) {
    return file
  }

  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = async () => {
      URL.revokeObjectURL(url)

      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(file)
        return
      }

      ctx.drawImage(img, 0, 0)

      const enc = resolveOutputEncoding(file)
      if (!enc) {
        resolve(file)
        return
      }

      let blob = await canvasToBlob(canvas, enc.outMime, enc.quality)
      let ext = enc.ext
      if (!blob && enc.outMime === MIME_WEBP) {
        blob = await canvasToBlob(canvas, MIME_PNG)
        ext = 'png'
      }
      if (!blob) {
        resolve(file)
        return
      }

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
      const outType = blob.type || enc.outMime
      const fixedFile = new File([blob], `${baseName}.${ext}`, {
        type: outType,
        lastModified: Date.now()
      })
      resolve(fixedFile)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(file)
    }

    img.src = url
  })
}
